import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import type { PullRow, ExpressionBuilder, Transaction, QueryRowType } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql, type Schema } from "../../zero/schema";
import { BodyMetric } from "../body-metric";
import { Unit } from "@corpuscomplete/core/common/unit";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import type { UserActor } from "@corpuscomplete/core/actor";
import { accessDenied, typedEntries } from "@corpuscomplete/core/utils";

export namespace BodyMetricSample {
  export const latest = defineQuery(
    z.object({ key: BodyMetric.keySchema, userId: z.uuid() }),
    ({ ctx, args: { key, userId } }) => {
      return zql.bodyMetricSample
        .whereExists("entry", (q) => {
          const r = q.where("userId", "=", userId);
          if (userId === ctx.actor.userId) {
            return r;
          }
          return r.where((e) => BodyMetricEntry.viewable(e, ctx.actor));
        })
        .whereExists("metric", (q) => q.where("key", "=", key))
        .orderBy("timeAt", "desc")
        .related("unit")
        .related("metric")
        .one();
    },
  );

  // TODO: Add unit override here as well - but after switching to unitKey as primary key, removing unitId
  export type Info = NonNullable<QueryRowType<typeof latest>>;
}

export namespace BodyMetricEntry {
  const build = <S extends SamplesSchema>(type: BodyMetric.GroupType, samplesSchema: S) => {
    const fromEntry = (
      entry: QueryRowType<typeof onDate> | undefined,
    ): (PullRow<"bodyMetricEntry", Schema> & { samples: SamplesValue<S> }) | null => {
      if (!entry) {
        return null;
      }
      const samples = {} as SamplesValue<S>;
      for (const key in samplesSchema.shape) {
        const schema = samplesSchema.shape[key as keyof typeof samplesSchema.shape];
        if (!schema) {
          continue;
        }
        const sample = entry.samples.find((s) => s.metric?.key === key);
        const parseResult = schema.safeParse(sample);
        if (parseResult.success) {
          // Don't use the parse result, because it only has the fields defined on the schema
          samples[key as keyof SamplesValue<S>] = sample as SamplesValue<S>[keyof SamplesValue<S>];
        } else {
          // TODO: What to do with extra fields? Missing, required fields?
          // If returning "null" in a case when entry arg is not null, need a Result type so that upsertOnDate can clean up the entry
          // OR pass in an extra arg to do field validation or not
          return null;
        }
      }
      return {
        ...entry,
        samples,
      };
    };
    // Need to explicitly type these or the downstream inferred types based on the generic S will not work
    const createOnDateSchema = z.object({
      entryId: z.uuid(),
      date: z.iso.date(),
      userId: z.uuid(),
      samples: samplesSchema,
    });
    const updateSamplesSchema = z.object({
      entryId: z.uuid(),
      samples: samplesSchema,
    });

    return {
      fromEntry,
      createOnDate: withZeroTransaction(createOnDateSchema, async ({ tx, ctx, args }) => {
        // eslint-disable-next-line cc/no-unsafe-as-assertion
        const { date, entryId, userId, samples } = args as {
          date: string;
          userId: string;
          entryId: string;
          samples: S["shape"];
        };
        const hasPermission = await canAddToUser(tx, ctx.actor, userId);
        if (!hasPermission) {
          accessDenied("You do not have permission to add a body metric entry to this user");
        }

        const [metrics, existingEntry] = await Promise.all([
          tx.run(zql.bodyMetric.where("key", "IS NOT", null)),
          tx.run(
            onDate.fn({
              ctx,
              args: {
                date,
                type,
                userId,
              },
            }),
          ),
        ]);
        if (existingEntry) {
          throw new Error(`BodyMetricEntry already exists for date "${date}"`);
        }

        const metricsByKey = createMetricsByKey(metrics);
        const timeAt = Temporal.PlainDate.from(date)
          .toZonedDateTime({
            timeZone: "UTC",
          })
          .toInstant();
        const timeNow = Temporal.Now.instant();

        await tx.mutate.bodyMetricEntry.insert({
          id: entryId,
          timeStart: timeAt,
          timeEnd: timeAt,
          timeZone: "UTC",
          timePrecision: "date",
          timeCreated: timeNow,
          timeUpdated: timeNow,
          type,
          userId,
        });

        for (const [bodyMetricKey, v] of typedEntries(samples)) {
          const sampleInput = v as unknown as z.infer<SampleSchema>;
          if (!sampleInput) {
            continue;
          }
          const metric = metricsByKey.get(bodyMetricKey);
          if (!metric) {
            throw new Error(`Metric "${bodyMetricKey}" not found`);
          }

          await tx.mutate.bodyMetricSample.insert({
            id: sampleInput.id,
            quantity: sampleInput.quantity,
            unitKey: sampleInput.unitKey,
            metricId: metric.id,
            timeAt,
            timeZone: "UTC",
            timePrecision: "date",
            timeCreated: timeNow,
            timeUpdated: timeNow,
            entryId,
          });
        }
      }),
      updateSamples: withZeroTransaction(updateSamplesSchema, async ({ tx, ctx, args }) => {
        // generic mutator args inference gap
        // eslint-disable-next-line cc/no-unsafe-as-assertion
        const { entryId, samples } = args as {
          entryId: string;
          samples: S["shape"];
        };
        const hasPermission = await canEdit(tx, ctx.actor, entryId);
        if (!hasPermission) {
          accessDenied("You do not have permission to edit this body metric entry");
        }

        const [metrics, existingEntry] = await Promise.all([
          tx.run(zql.bodyMetric.where("key", "IS NOT", null)),
          byId(tx, ctx.actor, entryId, type),
        ]);
        const existing = fromEntry(existingEntry);
        if (!existing) {
          accessDenied(`BodyMetricEntry (id: "${entryId}", type: "${type}") does not exist`);
        }
        const timeStart =
          typeof existing.timeStart === "number"
            ? Temporal.Instant.fromEpochMilliseconds(existing.timeStart)
            : existing.timeStart;
        const timeEnd =
          typeof existing.timeEnd === "number"
            ? Temporal.Instant.fromEpochMilliseconds(existing.timeEnd)
            : existing.timeEnd;
        if (Temporal.Instant.compare(timeStart, timeEnd) !== 0) {
          throw new Error(
            `BodyMetricEntry updateSamples not supported for entries with a time range`,
          );
        }
        const metricsByKey = createMetricsByKey(metrics);
        const timeNow = Temporal.Now.instant();

        await tx.mutate.bodyMetricEntry.update({
          id: entryId,
          timeUpdated: timeNow,
        });

        for (const [bodyMetricKey, v] of typedEntries(samples)) {
          const sampleInput = v as unknown as z.infer<SampleSchema>;
          if (!sampleInput) {
            continue;
          }
          const metric = metricsByKey.get(bodyMetricKey);
          if (!metric) {
            throw new Error(`Metric "${bodyMetricKey}" not found`);
          }

          const existingSample = existing.samples[bodyMetricKey] as
            | PullRow<"bodyMetricSample", Schema>
            | undefined;
          if (!existingSample) {
            await tx.mutate.bodyMetricSample.insert({
              id: sampleInput.id,
              timeAt: existing.timeStart,
              timeZone: existing.timeZone,
              timePrecision: existing.timePrecision,
              timeCreated: timeNow,
              timeUpdated: timeNow,
              metricId: metric.id,
              unitKey: sampleInput.unitKey,
              quantity: sampleInput.quantity,
              entryId,
            });
          } else {
            await tx.mutate.bodyMetricSample.update({
              id: existingSample.id,
              quantity: sampleInput.quantity,
              unitKey: sampleInput.unitKey,
              timeUpdated: timeNow,
            });
          }
        }
      }),
    };
  };

  export function viewable(eb: ExpressionBuilder<"bodyMetricEntry", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("userId", "=", actor.userId),
      eb.exists("grantsByUser", (q) =>
        AccessGrant.matching(
          q,
          actor,
          AccessGrant.criteria("user", "body_metric_entries", ["editor", "viewer"]),
        ),
      ),
    );
  }

  async function canEdit(tx: Transaction, actor: UserActor, entryId: string): Promise<boolean> {
    const entry = await tx.run(zql.bodyMetricEntry.where("id", "=", entryId).one());
    if (!entry) {
      return false;
    }
    if (entry.userId === actor.userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", entry.userId),
        actor,
        AccessGrant.criteria("user", "body_metric_entries", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  async function canAddToUser(tx: Transaction, actor: UserActor, userId: string): Promise<boolean> {
    if (actor.userId === userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", userId),
        actor,
        AccessGrant.criteria("user", "body_metric_entries", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  function createMetricsByKey(metrics: BodyMetric.Info[]) {
    const map = new Map<string, BodyMetric.Info>();
    for (const metric of metrics) {
      if (metric.key) {
        map.set(metric.key, metric);
      }
    }
    return map;
  }

  export type Delete = z.infer<typeof del.schema>;
  export const del = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      const hasPermission = await canEdit(tx, ctx.actor, id);
      if (!hasPermission) {
        accessDenied("You do not have permission to delete this body metric entry");
      }
      const samples = await tx.run(zql.bodyMetricSample.where("entryId", "=", id));
      await Promise.all(samples.map(({ id }) => tx.mutate.bodyMetricSample.delete({ id })));
      await tx.mutate.bodyMetricEntry.delete({ id });
    },
  );

  export type HeightQuantity = Unit.Quantity<"length", "cm" | "in">;
  export type HeightKey = HeightQuantity["unitKey"];
  export type WeightQuantity = Unit.Quantity<"mass", "lb" | "kg">;
  export type WeightKey = WeightQuantity["unitKey"];

  export const latest = defineQuery(
    z.object({ type: BodyMetric.groupTypeSchema, userId: z.uuid() }),
    ({ ctx, args: { type, userId } }) => {
      const q = zql.bodyMetricEntry
        .where("userId", "=", userId)
        .where("type", "=", type)
        .orderBy("timeStart", "desc")
        .related("samples", (q) => q.related("unit").related("metric"))
        .one();
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );

  export const onDate = defineQuery(
    z.object({
      date: z.iso.date(),
      type: BodyMetric.groupTypeSchema,
      userId: z.uuid(),
    }),
    ({ ctx, args: { date, type, userId } }) => {
      const timeForDate = Temporal.PlainDate.from(date).toZonedDateTime("UTC").toInstant();
      const q = zql.bodyMetricEntry
        .where("userId", "=", userId)
        .where("type", "=", type)
        .where("timePrecision", "=", "date")
        .where("timeStart", "=", timeForDate)
        .related("samples", (q) => q.related("unit").related("metric"))
        .one();
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );
  export const byId = (
    tx: Transaction,
    actor: UserActor,
    id: string,
    type: BodyMetric.GroupType,
  ) => {
    return tx.run(
      zql.bodyMetricEntry
        .where((e) => viewable(e, actor))
        .where("id", "=", id)
        .where("type", "=", type)
        .where("timePrecision", "=", "date")
        .related("samples", (q) => q.related("unit").related("metric"))
        .one(),
    );
  };

  type MaybeZodOptional<T extends z.ZodType> = T | z.ZodOptional<T>;

  type SampleSchema = z.ZodObject<{
    id: z.ZodUUID;
    quantity: z.ZodType<number>;
    unitKey: z.ZodType<Unit.Key>;
  }>;
  type SamplesSchema = z.ZodObject<{
    [K in BodyMetric.Key]?: MaybeZodOptional<SampleSchema>;
  }>;
  type SamplesValue<S extends SamplesSchema> =
    S extends z.ZodObject<infer T>
      ? {
          [K in keyof T]: T[K] extends z.ZodOptional<z.ZodType<infer U>>
            ? (PullRow<"bodyMetricSample", Schema> & U) | undefined
            : T[K] extends z.ZodType<infer U>
              ? PullRow<"bodyMetricSample", Schema> & U
              : PullRow<"bodyMetricSample", Schema>;
        }
      : never;

  export namespace Weight {
    export const samplesSchema = z
      .object({
        weight: z.object({
          id: z.uuid(),
          quantity: z.number().nonnegative(),
          unitKey: Unit.keySchema("mass").extract(["lb", "kg"]),
        }),
      })
      .check((ctx) => {
        const { quantity, unitKey } = ctx.value.weight;
        const max = BodyMetric.MASS_MAX[unitKey];
        if (quantity > max) {
          ctx.issues.push({
            code: "custom",
            message: `Weight must be at most ${max} ${unitKey}`,
            input: ctx.value.weight,
          });
        }
      }) satisfies SamplesSchema;
    export type Samples = z.infer<typeof samplesSchema>;

    export type Create = z.infer<typeof createOnDate.schema>;
    export type UpdateSamples = z.infer<typeof updateSamples.schema>;
    export const { fromEntry, createOnDate, updateSamples } = build("weight", samplesSchema);
    export type Info = NonNullable<ReturnType<typeof fromEntry>>;
  }

  export namespace BodyComposition {
    export const samplesSchema = z
      .object({
        height: z
          .object({
            id: z.uuid(),
            quantity: z.number().positive(),
            unitKey: Unit.keySchema("length").extract(["cm", "in"]),
          })
          .optional(),
        weight: z.object({
          id: z.uuid(),
          quantity: z.number().positive(),
          unitKey: Unit.keySchema("mass").extract(["lb", "kg"]),
        }),
        leanBodyMass: z
          .object({
            id: z.uuid(),
            quantity: z.number().positive(),
            unitKey: Unit.keySchema("mass").extract(["lb", "kg"]),
          })
          .optional(),
      })
      .check((ctx) => {
        if (ctx.value.height) {
          const { quantity, unitKey } = ctx.value.height;
          const max = BodyMetric.HEIGHT_MAX[unitKey];
          if (quantity > max) {
            ctx.issues.push({
              code: "custom",
              message: `Height must be at most ${max} ${unitKey}`,
              input: ctx.value.height,
            });
          }
        }

        {
          const { quantity, unitKey } = ctx.value.weight;
          const max = BodyMetric.MASS_MAX[unitKey];
          if (quantity > max) {
            ctx.issues.push({
              code: "custom",
              message: `Weight must be at most ${max} ${unitKey}`,
              input: ctx.value.weight,
            });
          }
        }

        if (ctx.value.leanBodyMass) {
          const { quantity, unitKey } = ctx.value.leanBodyMass;
          const max = BodyMetric.MASS_MAX[unitKey];
          if (quantity > max) {
            ctx.issues.push({
              code: "custom",
              message: `Lean body mass must be at most ${max} ${unitKey}`,
              input: ctx.value.leanBodyMass,
            });
          }

          if (ctx.value.weight.unitKey !== ctx.value.leanBodyMass.unitKey) {
            ctx.issues.push({
              code: "custom",
              message: "Weight and lean body mass must have the same unit",
              input: ctx.value,
            });
            return;
          }

          if (ctx.value.leanBodyMass.quantity >= ctx.value.weight.quantity) {
            ctx.issues.push({
              code: "custom",
              message: "Lean body mass must be less than weight",
              input: ctx.value,
            });
          }
        }
      }) satisfies SamplesSchema;
    export type Samples = z.infer<typeof samplesSchema>;

    export type Create = z.infer<typeof createOnDate.schema>;
    export type UpdateSamples = z.infer<typeof updateSamples.schema>;
    export const { fromEntry, createOnDate, updateSamples } = build(
      "body-composition",
      samplesSchema,
    );
    export type Info = NonNullable<ReturnType<typeof fromEntry>>;
  }
}
